import axios, { AxiosInstance } from 'axios';

import { addBreadcrumb } from 'config/sentryConfig';
import { getAxiosEventContext } from 'config/sentryEventContext';
import authManager from 'utils/authManager/authManager';

const getApiBreadcrumbLevel = (status?: number): 'error' | 'warning' => {
  if (status != null && status >= 500) {
    return 'error';
  }
  return 'warning';
};

const attachApiErrorBreadcrumb = (client: AxiosInstance) => {
  client.interceptors.response.use(
    response => response,
    error => {
      try {
        const axiosContext = getAxiosEventContext(error);
        if (axiosContext) {
          addBreadcrumb({
            type: 'http',
            category: 'api',
            level: getApiBreadcrumbLevel(axiosContext.status),
            data: axiosContext,
          });
        }
      } catch {
        // Breadcrumbs must never block API error handling.
      }
      return Promise.reject(error);
    },
  );
};

const axiosConfig = () => {
  axios.defaults.baseURL = import.meta.env.VITE_API_URL;
  axios.interceptors.request.use(config => {
    const token = authManager.getCookieAuthData()?.token;
    if (token) {
      // eslint-disable-next-line no-param-reassign
      config.headers = config.headers ?? {};
      // eslint-disable-next-line no-param-reassign
      config.headers.Authorization = `JWT ${token}`;
    } else if (config.headers) {
      // eslint-disable-next-line no-param-reassign
      delete config.headers.Authorization;
    }
    return config;
  });
  attachApiErrorBreadcrumb(axios);
  return axios;
};

const initWarehouseAxiosInstance = () => {
  const client = axios.create({
    baseURL: import.meta.env.VITE_WAREHOUSE_API_URL,
  });
  attachApiErrorBreadcrumb(client);
  return client;
};

export { initWarehouseAxiosInstance };

export default axiosConfig;
